import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Fetchable } from 'src/interfaces/api/api';
import useAuth from 'src/hooks/api/useAuth';

export interface Result {
  text: string;
  link: string;
}

export interface PaginatedResults {
  total: number;
  items: Result[];
  page: number;
}

interface ContextValue {
  contacts: Fetchable<PaginatedResults>;
  customers: Fetchable<PaginatedResults>;
  employees: Fetchable<PaginatedResults>;
  search: (query: string) => void;
  setContactPage: (newPage: number) => void;
  setCustomerPage: (newPage: number) => void;
  setEmployeePage: (newPage: number) => void;
}

const DEFAULT_LIMIT = 25;

const Context = React.createContext<ContextValue | undefined>(undefined);

interface ProviderProps {
  defaultQuery?: string;
}

export const SearchContextProvider: React.FC<ProviderProps> = (props) => {
  const { defaultQuery, children } = props;
  const [customerPage, setCustomerPage] = useState<number>(0);
  const [employeePage, setEmployeePage] = useState<number>(0);
  const [contactPage, setContactPage] = useState<number>(0);
  const [query, setQuery] = useState<string>(defaultQuery || '');
  const { fetch } = useAuth();

  const [contacts, setContacts] = useState<Fetchable<PaginatedResults>>({
    status: 'fetched',
    data: { total: 0, items: [], page: 0 },
  });
  const [customers, setCustomers] = useState<Fetchable<PaginatedResults>>({
    status: 'fetched',
    data: { total: 0, items: [], page: 0 },
  });
  const [employees, setEmployees] = useState<Fetchable<PaginatedResults>>({
    status: 'fetched',
    data: { total: 0, items: [], page: 0 },
  });

  const fetchEmployees = useCallback(async () => {
    setEmployees({ status: 'fetching' });

    const response = await fetch(
      `/api/globalSearchBar/{ employees(search:"${query}", limit: ${DEFAULT_LIMIT}, offset: ${
        employeePage * DEFAULT_LIMIT
      }){total, items{ id, firstname, lastname, user{ id, username }}}}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }
    );

    const json = await response.json();

    const result = json['data'] || {};

    const employees: Result[] = (result['employees']['items'] || []).map(
      (employee): Result => ({
        link: `/dashboard/user/${employee.user.id}`,
        text: `${employee.firstname} ${employee.lastname.toUpperCase()}`,
      })
    );

    setEmployees({
      status: 'fetched',
      data: { items: employees, total: result['employees']['total'], page: employeePage },
    });
  }, [employeePage, query]);

  const fetchContacts = useCallback(async () => {
    setContacts({ status: 'fetching' });

    const response = await fetch(
      `/api/globalSearchBar/{ contacts(search:"${query}", limit: ${DEFAULT_LIMIT}, offset: ${
        contactPage * DEFAULT_LIMIT
      }){total, items{ id, name, lastname }}}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }
    );

    const json = await response.json();

    const result = json['data'] || {};

    const contacts: Result[] = (result['contacts']['items'] || []).map(
      (contact): Result => ({
        link: `/dashboard/contact/${contact.id}`,
        text: `${contact.name} ${contact.lastname.toUpperCase()}`,
      })
    );

    setContacts({
      status: 'fetched',
      data: { items: contacts, total: result['contacts']['total'], page: contactPage },
    });
  }, [contactPage, query]);

  const fetchCustomers = useCallback(async () => {
    setCustomers({ status: 'fetching' });

    const response = await fetch(
      `/api/globalSearchBar/{ customers(search:"${query}", limit: ${DEFAULT_LIMIT}, offset: ${
        customerPage * DEFAULT_LIMIT
      }){total, items{id, installationSite{ socialReason }}}}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      }
    );

    const json = await response.json();

    const result = json['data'] || {};

    const customers: Result[] = (result['customers']['items'] || []).map(
      (customer): Result => ({
        link: `/dashboard/customer/${customer.id}`,
        text: `${customer.installationSite.socialReason}`,
      })
    );

    setCustomers({
      status: 'fetched',
      data: { items: customers, total: result['customers']['total'], page: customerPage },
    });
  }, [customerPage, query]);

  useEffect(() => {
    fetchEmployees();
  }, [query, employeePage]);

  useEffect(() => {
    fetchCustomers();
  }, [query, customerPage]);

  useEffect(() => {
    fetchContacts();
  }, [query, contactPage]);

  const contextValue = useMemo<ContextValue>(
    () => ({
      contacts,
      employees,
      customers,
      setContactPage,
      setCustomerPage,
      setEmployeePage,
      search: (query: string) => {
        setContactPage(0);
        setCustomerPage(0);
        setEmployeePage(0);
        setQuery(query);
      },
    }),
    [contacts, employees, customers]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default function useGlobalSearch(): ContextValue {
  const context = useContext(Context);

  if (context === undefined) {
    throw new Error('You must wrap components that call `useGlobalSearch` by a `SearchContextProvider`');
  }

  return context;
}
